import type {
  CreateResult,
  DeleteResult,
  GetResult,
  ListResult,
} from "@prolarus/shared";
import { tryCreate, tryDelete, tryGet, tryList, toQueryString } from "./_shared";

export interface UserModuleAccess {
  module_code: string;
  module_name: string;
  module_description: string | null;
  licensed_module_id: string;
  license_effective: string | null;
  access_source: "DIRECT" | "GROUP";
  group_id: string | null;
  group_name: string | null;
}

export interface LicensedModuleRow {
  id: string;
  module_id: string;
  module_code: string;
  module_name: string;
  module_description: string | null;
  effective: string | null;
  is_active: boolean;
}

export interface EnabledFeature {
  feature_code: string;
  feature_name: string;
  module_code: string;
}

export interface CompanyLicensedFeature {
  feature_code: string;
  feature_name: string;
  feature_description: string | null;
  module_code: string;
  is_enabled: boolean;
}

// Parameters prefixed with _ are kept for signature compatibility with the
// prior client-side service. The server clamps company_id to the JWT for
// every read/write here, so client-supplied company_id is intentionally
// ignored.
export const moduleAccessService = {
  hasModuleAccess(
    profileId: string,
    _companyId: string,
    moduleCode: string,
  ): Promise<GetResult<boolean>> {
    return tryGet<boolean>(
      `/api/module-access/check${toQueryString({ profile_id: profileId, code: moduleCode })}`,
    );
  },

  getUserModules(
    profileId: string,
    _companyId: string,
  ): Promise<ListResult<UserModuleAccess>> {
    return tryList<UserModuleAccess>(
      `/api/module-access${toQueryString({ profile_id: profileId })}`,
    );
  },

  isFeatureLicensedAndEnabled(
    _companyId: string,
    featureCode: string,
  ): Promise<GetResult<boolean>> {
    return tryGet<boolean>(
      `/api/licensed-features/check${toQueryString({ code: featureCode })}`,
    );
  },

  getEnabledFeatures(_companyId: string): Promise<ListResult<EnabledFeature>> {
    return tryList<EnabledFeature>("/api/licensed-features");
  },

  getCompanyLicensedFeatures(
    _companyId: string,
  ): Promise<ListResult<CompanyLicensedFeature>> {
    return tryList<CompanyLicensedFeature>(
      `/api/licensed-features${toQueryString({ include_disabled: "true" })}`,
    );
  },

  getRawLicensedFeature(
    _companyId: string,
    featureCode: string,
  ): Promise<GetResult<{ is_enabled: boolean; feature_name: string } | null>> {
    return tryGet<{ is_enabled: boolean; feature_name: string } | null>(
      `/api/licensed-features/raw${toQueryString({ code: featureCode })}`,
    );
  },

  listLicensedModules(
    _companyId: string,
  ): Promise<ListResult<LicensedModuleRow>> {
    return tryList<LicensedModuleRow>("/api/licensed-modules");
  },

  assignDirectModule(
    profileId: string,
    licensedModuleId: string,
    _companyId: string,
  ): Promise<CreateResult<{ id: string }>> {
    return tryCreate<{ id: string }>("/api/profile-licensed-modules", {
      profile_id: profileId,
      licensed_module_id: licensedModuleId,
    });
  },

  unassignDirectModule(
    profileId: string,
    licensedModuleId: string,
    _companyId: string,
  ): Promise<DeleteResult> {
    return tryDelete(
      `/api/profile-licensed-modules${toQueryString({
        profile_id: profileId,
        licensed_module_id: licensedModuleId,
      })}`,
    );
  },
};
